// Het filter "Propositie" voor de schermen van de huidige weergave (Trends,
// Skills, Bronnen, Verkennen). ADR 2026-10-23-ict-los-platform; dezelfde regels
// als web/src/nieuw/propositie.tsx:
//
//  · de keuzes komen uit /api/meta `proposities` (alleen zichtbare sectoren);
//  · met één keuze (vandaag: alleen ICT) verschijnt het filter niet en vraagt
//    het scherm precies wat het vóór de ADR vroeg — niets voorgeselecteerd;
//  · met twee of meer opent het scherm op de propositie van de gebruiker
//    (`startSector` uit /api/mij) en zet "Alles wissen" hem daarop terug.

import { useEffect, useState } from "react";

import type { MetaResponse } from "../contracts";
import { useActieveSector, vastePropositie } from "./sectorkeuze";

export type PropositieFilter = {
  /** Toont het scherm het filter? */
  toon: boolean;
  /** De namen in het menu. */
  opties: string[];
  /** De gekozen namen. */
  gekozen: string[];
  zet: (namen: string[]) => void;
  /** Terug naar de beginstand (de eigen propositie). */
  terug: () => void;
  /** De sectorsleutels voor de API. */
  sleutels: string[];
  /** false zolang de beginstand nog gezet moet worden: zo vraagt het scherm niet eerst alles. */
  klaar: boolean;
};

/** De naam in het menu. Een propositie die alleen beheer ziet (ICT, pilot
 *  26-10 baan 2B) draagt "(intern)"; een pilotgebruiker krijgt die nooit. */
export function propositieNaam(p: { naam: string; intern?: boolean }): string {
  return p.intern ? `${p.naam} (intern)` : p.naam;
}

export function usePropositieFilter(meta: MetaResponse | null): PropositieFilter {
  const start = useActieveSector();
  const vast = vastePropositie();
  const lijst = (meta?.proposities ?? []).map((p) => ({ ...p, naam: propositieNaam(p) }));
  const toon = lijst.length > 1;
  const beginstand = (): string[] => {
    const eigen = lijst.find((p) => p.sector === start);
    return eigen ? [eigen.naam] : [];
  };
  const [gekozen, setGekozen] = useState<string[]>([]);
  const [gezet, setGezet] = useState(false);
  useEffect(() => {
    if (vast || !toon || gezet || !start) return;
    setGekozen(beginstand());
    setGezet(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toon, start]);
  // Een vaste propositie (accountmanager, business partner): geen menu, geen
  // keuze, geen pil. Het scherm vraagt altijd de eigen propositie.
  if (vast) {
    return { toon: false, opties: [], gekozen: [], zet: () => {}, terug: () => {}, sleutels: [vast], klaar: meta !== null };
  }
  return {
    toon,
    opties: lijst.map((p) => p.naam),
    gekozen,
    zet: setGekozen,
    terug: () => setGekozen(toon ? beginstand() : []),
    sleutels: gekozen.map((n) => lijst.find((p) => p.naam === n)?.sector).filter((s): s is string => !!s),
    klaar: meta !== null && (!toon || gezet),
  };
}

/** De sectoren van het filter Sector: de bedrijfstakken, zonder de pilotsectoren
 *  (die staan onder Propositie). Vandaag dezelfde zestien als altijd.
 *
 *  Pilot 26-10 (baan 2B): ICT is interne testdata. Mag dit verzoek ICT niet
 *  zien (niet in `zichtbareSectoren`), dan staat ICT ook niet in het filter;
 *  beheer ziet hem met "(intern)". */
export function bedrijfstakSectoren(meta: MetaResponse | null) {
  const pilot = new Set((meta?.proposities ?? []).map((p) => p.sector));
  return (meta?.sectors ?? [])
    .filter((s) => !pilot.has(s.key) || s.key === "ict")
    .filter((s) => !verborgenIct(meta, s.key))
    .map((s) => (meta?.interneSectoren?.includes(s.key) ? { ...s, label: `${s.label} (intern)` } : s));
}

/** ICT in een antwoord dat ICT niet toont: de sleutel hoort dan nergens in een menu. */
export function verborgenIct(meta: MetaResponse | null | undefined, key: string): boolean {
  return key === "ict" && !!meta?.zichtbareSectoren && !meta.zichtbareSectoren.includes("ict");
}

/** "Eén sector is gemeten (ICT)" — uit de data in plaats van vast in de tekst. */
export function gemetenZin(meta: MetaResponse | null): string {
  const gemeten = [
    ...(meta?.sectors ?? []).filter((s) => s.active && !verborgenIct(meta, s.key)).map((s) => s.label),
    ...(meta?.proposities ?? [])
      .filter((p) => !(meta?.sectors ?? []).some((s) => s.key === p.sector))
      .map((p) => p.naam),
  ];
  // Niets gemeten, of de meta is er nog niet: nooit terugvallen op "ICT" (baan
  // 2B: een pilotgebruiker ziet die naam nergens).
  if (!gemeten.length) return "Nog geen sector gemeten";
  const namen = gemeten;
  return namen.length === 1
    ? `Eén sector is gemeten (${namen[0]})`
    : `${namen.length} sectoren zijn gemeten (${namen.join(", ")})`;
}
